import { useState } from 'react';
import type { WorkflowTestCapture } from '@vagusx/clients';
import { Check, ClipboardCopy, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useFetchWorkflowTestCapture } from '@/hooks/use-workflows';
import { downloadBlob } from '@/lib/download';
import { captureFilename, captureJson, captureMarkdown } from './test-capture';

/**
 * Turn a finished test call into a ticket (ADR-309).
 *
 * A test changes nothing, so when one goes wrong there is nothing else to look
 * at. This hands the tester the whole call — their note, the transcript, the
 * worker's trace, the report and the definition that ran — as Markdown to
 * paste into a ticket or to Claude, or as a JSON file. Fetched on each press,
 * so a trace that arrived after the call ended is included.
 */
/**
 * Put text that is still being fetched on the clipboard. Safari allows a
 * clipboard write only inside the click, and a fetch outlasts that, so the
 * write starts at once with a `ClipboardItem` whose content is the promise.
 * Where there is no `ClipboardItem`, it falls back to writing the text once it
 * arrives.
 */
function writeClipboard(text: Promise<string>): Promise<void> {
  if (typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard.write === 'function') {
    return navigator.clipboard.write([
      new ClipboardItem({ 'text/plain': text.then((t) => new Blob([t], { type: 'text/plain' })) }),
    ]);
  }
  return text.then((t) => navigator.clipboard.writeText(t));
}

export function TestTicket({
  workflowId,
  interactionId,
}: {
  workflowId: string;
  interactionId: string;
}): React.ReactElement {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'copy' | 'download' | null>(null);
  const [copied, setCopied] = useState(false);
  const fetchCapture = useFetchWorkflowTestCapture(workflowId, interactionId);

  const run = async (kind: 'copy' | 'download'): Promise<void> => {
    setBusy(kind);
    // Set once the capture arrives, so a failure says whether the server or
    // the browser refused.
    const got: { capture: WorkflowTestCapture | null } = { capture: null };
    const capture = fetchCapture().then((c) => {
      got.capture = c;
      return c;
    });
    try {
      if (kind === 'copy') {
        // Started in the click itself, before anything is awaited (`writeClipboard`).
        await writeClipboard(capture.then((c) => captureMarkdown(c, note)));
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
        toast.success(
          got.capture?.trace
            ? 'Copied the whole call for a ticket'
            : 'Copied, but the worker trace has not arrived yet. Try again in a few seconds.'
        );
      } else {
        const c = await capture;
        downloadBlob(
          new Blob([captureJson(c, note)], { type: 'application/json' }),
          captureFilename(c)
        );
      }
    } catch {
      // Handled here: a refused clipboard is not an API error, and the global
      // handler covers mutations, not this fetch.
      toast.error(
        !got.capture
          ? 'Could not load this call from the server. Try again in a moment.'
          : kind === 'copy'
            ? 'Could not copy. Try Download instead.'
            : 'Could not prepare the file.'
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-2 border-t p-4">
      <h2 className="text-sm font-semibold">Report a problem</h2>
      <p className="text-xs text-muted-foreground">
        Copies everything this call left behind: the transcript, what the agent did at each step,
        its errors, and the workflow that ran. Paste it into a ticket.
      </p>
      <Label htmlFor="test-ticket-note" className="text-xs">
        What went wrong?
      </Label>
      <Textarea
        id="test-ticket-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What you expected, and what happened instead"
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void run('copy')} disabled={busy !== null}>
          {busy === 'copy' ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : copied ? (
            <Check className="mr-2 h-4 w-4" />
          ) : (
            <ClipboardCopy className="mr-2 h-4 w-4" />
          )}
          Copy trace to clipboard
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void run('download')}
          disabled={busy !== null}
        >
          {busy === 'download' ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}
